import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';

import { AuthProvider } from '@/context/AuthContext';
import { InitialLoadingProvider } from '@/context/InitialLoadingContext';
import { initFontLoader } from '@/utils/fontLoader';
import {
  initPerformanceMonitoring,
  markPerformance,
  reportCustomMetric,
  resetPerformanceMeasurements,
} from '@/utils/performanceMonitor';
import { initPWA } from '@/utils/pwa';
// import { initVersionCheck } from '@/utils/version-check';

import App from './App.tsx';
import ThemeContainer from './theme/themeProvider';

import 'nprogress/nprogress.css';
import './index.css';

// Clear any previous performance measurements to start fresh
resetPerformanceMeasurements();

// Mark the start of the app initialization process
markPerformance('app-bootstrap-start');
markPerformance('app-init');

// Initialize performance monitoring
initPerformanceMonitoring();

// Initialize PWA service worker (production only)
if (import.meta.env.PROD) {
  initPWA();
}

// Initialize version checking (polling safety net) - DISABLED
// The modern VersionCheck component handles this with proper auth checks
// initVersionCheck();

// Initialize font loader with production-optimized settings
initFontLoader({
  debug: import.meta.env.DEV,
  timeout: 3000,
  onLoaded: () => {
    markPerformance('fonts-loaded');
    if (import.meta.env.DEV) {
      console.debug('✅ Fonts loaded successfully');
    }
  },
  onError: (error) => {
    if (import.meta.env.DEV) {
      console.warn('⚠️ Font loading error:', error.message);
    }
  },
});

// Track initial page load navigation attempt
const initialNavigationStart = performance.now();
// Optimized QueryClient with performance settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      gcTime: 1000 * 60 * 10, // 10 minutes
    },
  },
});

// Track time to initial render
markPerformance('app-render-start');

ReactDOM.createRoot(document.getElementById('root')!).render(
  import.meta.env.PROD ? (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <BrowserRouter>
            <ThemeContainer>
              <InitialLoadingProvider>
                <AuthProvider>
                  <App />
                </AuthProvider>
              </InitialLoadingProvider>
            </ThemeContainer>
          </BrowserRouter>
        </HelmetProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </React.StrictMode>
  ) : (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <BrowserRouter>
          <ThemeContainer>
            <InitialLoadingProvider>
              <AuthProvider>
                <App />
              </AuthProvider>
            </InitialLoadingProvider>
          </ThemeContainer>
        </BrowserRouter>
      </HelmetProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  ),
);

// Mark the end of bootstrapping
markPerformance('app-bootstrap-end');

// Log bootstrap time in development
if (import.meta.env.DEV) {
  // Use setTimeout to ensure this runs after render is complete
  setTimeout(() => {
    try {
      const bootstrapTime = performance.measure(
        'app-bootstrap-time',
        'app-bootstrap-start',
        'app-bootstrap-end',
      ).duration;

      // Calculate time for initial navigation
      const initialNavTime = performance.now() - initialNavigationStart;

      // Report the metrics

      // Record this as a custom metric
      reportCustomMetric('initial-bootstrap-time', bootstrapTime);
      reportCustomMetric('initial-navigation-start-time', initialNavTime);
    } catch (error) {
      console.error('Failed to measure bootstrap time', error);
    }
  }, 0);
}
